import React, { useState, useRef, useEffect } from 'react';
import { computedTitleLines } from '../utils/titleComputed';
import { canonRate, ratePct, buydownRate, formatRate, monthlyPI } from '../constants/rateFormat';
import { cardPaymentLines } from '../utils/paymentBreakdown';   // the monthly-payment lines (2026-11-01)
import { DOWN_PAYMENT_DUE_PHRASE } from '../constants/downPaymentDue';
import { CLOSING_COSTS_EXPANDER_LABEL, INSPECTION_LINE } from '../constants/closingCostsCopy';

// ── SmartEstimate option card — the real borrower-facing estimate card ────────
// Extracted verbatim from Dashboard.js so it can be reused both post-approval
// (HeroApproved) and pre-approval (SmartEstimatePreview). Tooltip + the tooltip
// copy constants moved with it because they are used ONLY by this card.
// Keep the compute in lockstep with backend/utils/generateSmartEstimatePdf.js.

// ── Tooltip component ─────────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState('above');

  // ⚠️ onBlur IS THE WRONG MECHANISM HERE, NOT A BROKEN ONE — do not patch it
  // back. On iOS, tapping a non-focusable region (which is most of this card)
  // does not move focus, so the button never blurs and the tooltip stays open
  // with no way to close it. Safari also does not focus a <button> on tap in
  // every configuration, so blur may never have been armed in the first place.
  // The mechanism has to be "did a tap land outside this tooltip", which is a
  // pointer question, not a focus question.
  //
  // pointerdown, not click: it fires on touch without waiting for the 300ms
  // click resolution, and it fires even when the tap lands on something that
  // swallows click. Capture phase so a child's stopPropagation cannot strand
  // the tooltip open.
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!visible) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setVisible(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setVisible(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [visible]);
  const btnRef = useRef(null);

  const handleToggle = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPosition(rect.top < 150 ? 'below' : 'above');
    }
    setVisible(v => !v);
  };

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        ref={btnRef}
        onClick={handleToggle}
        aria-expanded={visible}
        aria-label={visible ? 'Hide explanation' : 'Show explanation'}
        style={{
          width: '14px', height: '14px', borderRadius: '50%',
          background: '#e5e7eb', border: 'none', cursor: 'pointer',
          fontSize: '9px', fontWeight: '700', color: '#6b7280',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginLeft: '4px', padding: 0,
        }}
      >
        ?
      </button>
      {visible && btnRef.current && (
        <div style={{
          position: 'fixed',
          zIndex: 9999,
          background: '#1f2937',
          color: '#ffffff',
          fontSize: '12px',
          lineHeight: '1.5',
          padding: '10px 14px',
          borderRadius: '8px',
          maxWidth: '260px',
          width: '260px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          top: position === 'above'
            ? btnRef.current.getBoundingClientRect().top - 10
            : btnRef.current.getBoundingClientRect().top + 30,
          left: Math.min(btnRef.current.getBoundingClientRect().left, window.innerWidth - 270),
          transform: position === 'above' ? 'translateY(-100%)' : 'translateY(20px)',
        }}>
          {text}
          <div style={{
            position: 'absolute',
            ...(position === 'above' ? { bottom: '-6px' } : { top: '-6px' }),
            left: '16px',
            width: 0, height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            ...(position === 'above'
              ? { borderTop: '6px solid #1f2937' }
              : { borderBottom: '6px solid #1f2937' }),
          }} />
        </div>
      )}
    </span>
  );
}

// ⚠️ NO PERCENTAGE IN THE TOOLTIP. It said "At 2%" and would have needed editing
// in lockstep with the constant forever; the figure is already on the row this
// tooltip is attached to.
const PROGRAM_FEE_TOOLTIP = 'Program fees include standard loan costs such as origination, processing, and underwriting. They are in line with what most lenders charge and are clearly disclosed upfront—no hidden fees.';

const PURCHASE_PRICE_TOOLTIP = "The price of the home you've selected. It is the same as the contract price in Doorly's purchase agreement with the seller — Doorly does not add a markup. This is the amount used to calculate your loan and monthly payment.";

const MONTHLY_PAYMENT_TOOLTIP = 'Your estimated monthly payment includes principal, interest, property taxes, and insurance. Final payment details will be confirmed before closing.';

const INTEREST_RATE_TOOLTIP = 'The estimated rate for your loan based on your profile and current market conditions. Your final rate will be confirmed as you move closer to closing.';

// ⚠️ "Purchase Initiation Deposit", NOT "Offer Initiation Fee". This tooltip was the
// last live place in the app still using the retired name — the rename that
// produced the current term reached the program agreement, the payment modal,
// the wire instructions and every page, and missed this string. A borrower
// reading it here and "Purchase Initiation Deposit" on the payment screen has no
// way to know they are the same charge. backend/test/feeNameParity.test.js
// fails if the old name returns.
// 🔑 DUE 3 BUSINESS DAYS AFTER THE CONTRACT'S EFFECTIVE DATE (Jesse, 2026-11-01 — the program agreement §2), not
// "immediately upon offer acceptance": one constant, constants/downPaymentDue (mirrors the backend's, which sets the date).
const DOWN_PAYMENT_TOOLTIP = `The portion of your purchase price you pay up front. Your down payment is due ${DOWN_PAYMENT_DUE_PHRASE}, wired to secure your contract. The Purchase Initiation Deposit you already paid is credited toward it on your Closing Disclosure.`;
const TOTAL_CASH_TOOLTIP = `The total amount you'll need to bring to closing, including your down payment and all closing costs. Your down payment is due ${DOWN_PAYMENT_DUE_PHRASE}; the remaining cash to close is due 3 business days prior to your close date. This will be finalized and clearly outlined before closing.`;

// (2026-10-21) The resale adjustment tooltip is gone: there is no resale adjustment on a new SmartEstimate.

// ⚠️ showSelect GATES THE BUTTON, IT IS NOT REMOVED. On the approved-borrower
// card onSelect opens the program terms and leads to the signature that accepts
// the loan — deleting it would break signing. On the pre-approval SmartEstimate
// preview the same prop was `noop`: a prominent "Select Option 1" that did
// literally nothing when tapped. Same component, two contexts, one of which has
// no action to offer — so the caller says whether there is one.
// ── 🔑 ONE SMARTESTIMATE, AND THE FIGURES ARRIVE FROM THE SERVER ────────────
//
// This card took `optionNum`, `resalePct` and `showFee` from whoever mounted it,
// and multiplied by a hardcoded 0.02 inside. Two callers passed those props out
// of literals written by hand, so the percentages a borrower saw lived in three
// files and agreed by habit.
//
// ⚠️ THE DEFAULTS ARE THE LAST RESORT, NOT THE SOURCE. They exist so the card
// renders before GET /api/fee-schedule resolves; `terms` overrides them the
// moment it arrives. They are the only figures left in this file and they are
// the ones that must never be trusted to be current.
function SmartEstimateOptionCard({ est, terms, onSelect, showSelect = true, appraisalFee = null, recordingFee = null,
  creditReportFee = null, titleLines = null, titleComputed = null,
  // 🔑 variant 'concise' (guided step 8, Jesse 2026-10-25 — the approved mockups): the figures a borrower decides on;
  // the itemized fees wait for the Loan Estimate. Classic keeps the full card.
  variant = 'full',
  // 🔑 "See all estimated closing costs" (Jesse, 2026-11-01 — APPROVED by Hannah 2026-11-01, constants/smartEstimateFaq
  // FINE_PRINT_V2_APPROVED): the itemized lines behind "Other closing costs and prepaids", from THIS card's figures.
  showCostDetails = false, costsOpenInitially = false,
  // ── 🚨 HOW MANY POINTS THIS BORROWER MAY ACTUALLY BUY ────────────────────
  //
  // The control offered 0-4 to everyone out of a hardcoded array. Four points on
  // a $315,000 loan is $12,600 of points and fees ON TOP of the Program Fee and
  // any broker compensation — past the federal high-cost trigger. A borrower
  // could tap a button and make their own loan un-originable.
  //
  // 🔑 PER FILE, FROM THE SERVER. It depends on the loan amount, the Program Fee
  // actually disclosed, whether a broker is paid, and how far the undiscounted
  // rate sits from APOR — GET /api/borrower/buydown-cap, the same function the
  // acceptance handler enforces with.
  //
  // ⚠️ THE DEFAULT IS ZERO, NOT FOUR. Before the cap arrives, and on the
  // pre-approval preview where there is no file to cap, no buydown is offered —
  // defaulting to the old maximum would restore the unguarded control on exactly
  // the files least is known about.
  buydownCap = null }) {
  // 🔑 FROM THE SERVER ONLY (GET /api/fee-schedule smartEstimate): no stale default percentage.
  const programFeePct = terms?.programFeePct != null ? Number(terms.programFeePct) : null;
  const programFeeBasis = terms?.programFeeBasis || null;
  const [buydownOpen, setBuydownOpen] = React.useState(false);
  const [costsOpen, setCostsOpen] = React.useState(!!costsOpenInitially);
  const [buydownPoints, setBuydownPoints] = React.useState(0);
  // ⚠️ CLAMPED, NOT JUST DEFAULTED. The cap arrives after the first render, and a
  // borrower who had already chosen 3 must not keep a selection the server will
  // refuse — the acceptance handler checks the same number.
  const maxBuydownPoints = Math.max(0, Number(buydownCap?.maxPoints) || 0);
  React.useEffect(() => {
    if (buydownPoints > maxBuydownPoints) setBuydownPoints(maxBuydownPoints);
  }, [maxBuydownPoints, buydownPoints]);

  // When HeroApproved passes an override_purchase_price (the borrower entered
  // a target home price in Phase 1), that value IS the Doorly acquisition
  // price (consistent with Atlas's atlas_max_purchase_price model). The
  // borrower-facing "Your Purchase Price" is that price itself (D3, 2026-11-01: no resale factor).
  const overridePP = Number(est.override_purchase_price) || 0;
  const doorlyPrice = overridePP > 0
    ? overridePP
    : (est.doorly_purchase_price || 0);
  const purchasePrice = overridePP > 0
    ? Math.round(overridePP)
    : (doorlyPrice > 0 ? Math.round(doorlyPrice) : (est.max_approved_price || 0));
  // ── 🚨 A MISSING DOWN PAYMENT PERCENT IS NOT 0% ─────────────────────────
  //
  // GET /status resolves down_payment_pct to NULL when it cannot derive one —
  // correctly, it refuses to invent. This line then turned that refusal into
  // `0`, and the three lines below turned 0 into a $0 down payment and a loan
  // equal to the ENTIRE purchase price. The borrower signed that card while the
  // server recorded 10%: the signed document and the stored record disagreed
  // about the second-largest number on the file.
  //
  // 🔑 UNKNOWN IS ITS OWN STATE, and the whole card goes with it — down
  // payment, loan amount, cash to close and the monthly payment all derive from
  // this one figure, so showing any of them would be showing a number built on
  // nothing. Accept is DISABLED, not hidden: a control that vanishes reads as a
  // step that does not apply, and this one applies and is not ready.
  const dpUnknown = est.down_payment_pct == null && !est.down_payment;
  const downPct = est.down_payment_pct || 0;
  const baseRate = est.approved_interest_rate || est.interest_rate || 0;
  const downAmt = downPct > 0 ? Math.round(purchasePrice * downPct / 100) : (est.down_payment ? Number(est.down_payment) : 0);
  const loanAmt = purchasePrice - downAmt;
  // 🚨 THE PROGRAM FEE IS A PERCENTAGE OF THE LOAN AMOUNT (Jesse, 2026-10-15). This card computed
  // it on the purchase price — $6,000 instead of $4,800 on a $300,000 home with $60,000 down —
  // while the LE disclosed the loan-based figure.
  const programFee = Math.round((programFeeBasis === 'price' ? purchasePrice : loanAmt) * ((programFeePct || 0) / 100));
  // 🔑 THE SAME ITEMIZED LINES THE LE CARRIES, every figure from GET /api/fee-schedule
  // (constants/feeSchedule and constants/tiagoFeeSchedule.js) so this estimate never drifts
  // from the LE/CD: appraisal, credit report, the Section B title lines, recording.
  // 🚨 2026-10-15 (Jesse): no "title 1%", no bundled figure, NO origination fee and NO
  // inspection — Doorly pays for the inspection — in this card or its total.
  // 🚨 NO FALLBACK FIGURE: until GET /api/fee-schedule has answered, it says so rather than guessing.
  const known = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));
  // 🔑 The fixed lines served as titleLines, plus the per-loan line(s) (T-19, priced from TDI's rates on THIS
  // loan amount — utils/titleComputed.js, from the served titleComputed inputs).
  const lines = Array.isArray(titleLines) ? [...titleLines, ...computedTitleLines(titleComputed, loanAmt)] : [];
  // 🔑 THE LE'S OTHER COSTS, FROM THE LE'S OWN CONSTANTS (Jesse, 2026-10-15: "align its monthly
  // taxes and insurance, and its cash-to-close components (prepaid interest, prepaid taxes,
  // escrow deposit), with the LE's sources so the two never disagree"). This mirrors
  // backend/constants/feeSchedule.js leOtherCostsEstimate — the function the SmartEstimate PDF
  // and the acceptance snapshot call — from the served `leBasis`; it is a mirror because this
  // card re-prices live as the borrower moves the price. backend/test/leSurfaceAgreement renders
  // this card and fails if any figure here differs from the native LE (computeLEFigures).
  const B = terms?.leBasis || null;
  const hoiRate = B?.annualHoiRate;
  const hoiMonths = B?.prepaidHoiMonths;
  const basisKnown = !!B && ['annualTaxRate', 'annualHoiRate', 'prepaidHoiMonths', 'prepaidTaxMonths', 'escrowHoiMonths', 'escrowTaxMonths',
    'floodFee', 'dayCountBasis'].every((k) => known(B[k]));
  const feesKnown = programFeePct !== null && !!programFeeBasis && basisKnown && known(appraisalFee) && known(recordingFee) && known(creditReportFee)
    && lines.length > 0 && lines.every((l) => known(l.amount));
  const traditionalAppraisalFee = Number(appraisalFee);
  const titleTotal = lines.reduce((t, l) => t + Math.round(Number(l.amount) * 100), 0) / 100;
  const r2 = (x) => Math.round(x * 100) / 100;
  const monthlyTax = r2(purchasePrice * Number(B?.annualTaxRate) / 12);
  const monthlyHOI = r2(purchasePrice * Number(hoiRate) / 12);
  // Section F: the first-year HOI premium (it was 0.5% of the price, $1,500 on $300,000 against
  // the LE's $1,200), prepaid interest and prepaid taxes. Section G: the initial escrow deposit.
  const hoiFirstYear = r2(monthlyHOI * Number(hoiMonths));
  const prepaidTax = r2(monthlyTax * Number(B?.prepaidTaxMonths));
  const escrowDeposit = r2(monthlyHOI * Number(B?.escrowHoiMonths) + monthlyTax * Number(B?.escrowTaxMonths));
  // Prepaid interest runs from the closing date to the first of the next month
  // (backend/utils/prepaidInterest.computePrepaidInterest). 🚨 With no closing date it is UNKNOWN
  // and shown so — the LE cannot be computed without the date either, and the 31-day projection
  // estimate is barred from borrower documents.
  const rateForInterest = ratePct(baseRate) || 0;
  const closeMs = est.date_closing ? Date.parse(String(est.date_closing).slice(0, 10) + 'T00:00:00Z') : NaN;
  const interestDays = Number.isFinite(closeMs)
    ? Math.round((Date.UTC(new Date(closeMs).getUTCFullYear(), new Date(closeMs).getUTCMonth() + 1, 1) - closeMs) / 86400000)
    : null;
  const prepaidInterest = interestDays !== null && loanAmt > 0 && rateForInterest > 0
    ? r2((loanAmt * (rateForInterest / 100)) / Number(B?.dayCountBasis) * interestDays) : null;
  const floodFee = Number(B?.floodFee) || 0;
  // 🔑 The LE's J less the Program Fee: Sections B + C + E + F + G. With the down payment and the
  // Program Fee it is the LE's Estimated Cash to Close (before any deposit already paid).
  const closingCosts = r2(traditionalAppraisalFee + Number(creditReportFee) + floodFee + titleTotal
    + Number(recordingFee) + hoiFirstYear + (prepaidInterest || 0) + prepaidTax + escrowDeposit);
  const baseTotalCash = downAmt + programFee + closingCosts;
  // 🔑 THE STORED RATE, ONE FORMULA (the rate fix, 2026-10-25): constants/rateFormat mirrors backend/utils/rate.
  const rateDecimal = canonRate(baseRate) || 0;
  // Estimated escrow: the LE's monthly tax and HOI (above). It was 1.6% tax and a flat $150 HOI —
  // $400 and $150 on $300,000 against the LE's $500 and $100. The "Est. Monthly Payment" label
  // is true PITI; the tooltip says it includes T&I, so the value must too.
  const propertyTaxMo = monthlyTax;
  const hoiMo = monthlyHOI;
  // HOA when the decision knows it (the stored breakdown); 0 otherwise. (It was always 0 here.)
  const hoaMo = Number(est.payment_breakdown && est.payment_breakdown.hoa) || 0;
  // (PMI removed — non-QM loans can't bind MI. Escrow is property tax
  //  + HOI + HOA only; the SmartEstimate option-card monthly is
  //  P&I + escrow.)
  const escrowMo = propertyTaxMo + hoiMo + hoaMo;
  const basePI = Math.round(monthlyPI(loanAmt, rateDecimal));
  const baseMonthly = basePI + escrowMo;

  // Buydown calculation. Buydown only affects P&I; escrow is unchanged, so
  // the savings figure cleanly reflects the rate reduction.
  const calcBuydown = (pts) => {
    if (!pts || pts === 0) return null;
    const newRate = buydownRate(rateDecimal, pts);
    const newPI = Math.round(monthlyPI(loanAmt, newRate));
    const newMonthly = newPI + escrowMo;
    const savings = baseMonthly - newMonthly;
    const cost = Math.round(loanAmt * pts / 100);
    const breakEven = savings > 0 ? Math.ceil(cost / savings) : null;
    return { pts, cost, newRate, newPI, newMonthly, savings, breakEven, newTotalCash: baseTotalCash + cost };
  };
  const bd = calcBuydown(buydownPoints);
  const totalCashToClose = bd ? bd.newTotalCash : baseTotalCash;
  const shownPI = bd ? bd.newPI : basePI;
  // 🔑 THE LINES (2026-11-01): the decision's stored breakdown for this price, else the same rounding re-priced live —
  // P&I, taxes, insurance, HOA (when > 0); no mortgage insurance line (the product has none). The headline is their total.
  const payLines = cardPaymentLines({ stored: est.payment_breakdown || null, price: purchasePrice, buydownPI: bd ? bd.newPI : null,
    pi: basePI, tax: propertyTaxMo, hoi: hoiMo, hoa: hoaMo });
  const monthlyPayment = payLines.total;
  const displayRateStr = formatRate(bd ? bd.newRate : rateDecimal);
  const origRateStr = formatRate(rateDecimal);

  // 🔑 WHOLE DOLLARS, ROUNDED TO THE NEAREST, AS THE LE PRINTS THEM (§1026.37(o)(4), 2026-10-15).
  const fmt$ = (v) => v ? `$${Math.round(Number(v)).toLocaleString('en-US')}` : '—';
  const fmtFee = fmt$;

  if (!feesKnown) {
    return <div className="text-sm text-gray-500 px-4 py-6">Loading your estimate…</div>;
  }

  if (variant === 'concise') {
    // 🔑 THE APPROVED STEP-8 CARD: white, a thin doorly-yellow top edge, black text, Inter (inherited); the Program Fee
    // on its own row; the buydown behind "Buy down my rate"; Accept is the one button.
    const row = { display: 'flex', justifyContent: 'space-between', gap: 12 };
    const bold = { fontWeight: 700 };
    const big = { ...row, fontSize: 15 };
    const groupHead = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', paddingTop: 6 };
    const ptLabel = (n) => (n === 0 ? 'None' : `${n} point${n === 1 ? '' : 's'}`);
    const feeBasis = programFeeBasis === 'price' ? 'purchase price' : 'loan amount';
    return (
      <div data-testid="se-card" data-variant="concise" style={{ boxSizing: 'border-box', width: '100%', borderRadius: 16, overflow: 'hidden', background: '#fff',
        border: '1.5px solid #e6e6e6', borderTop: '5px solid #fffd5b', color: '#000' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '12px 16px', fontSize: 15, fontWeight: 800,
          borderBottom: '1px solid #e6e6e6' }}>
          <span>Your SmartEstimate</span>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{fmt$(purchasePrice)}</span>
        </div>
        {/* 🚨 THE FOUR GROUPS, IN THE SIGNED PDF'S ORDER (Jesse, 2026-10-25): concise, but loan amount and rate stay
            last and away from the payment — §1026.19(e)(2)(ii), not H-24's Loan Terms box (test/smartEstimateFormat). */}
        <div style={{ padding: '12px 16px 4px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
          <div style={groupHead}>YOUR PRICE</div>
          <div style={row}><span>Purchase price</span><span style={bold}>{fmt$(purchasePrice)}</span></div>
          <div style={{ ...row, fontSize: 12 }}><span>Doorly&rsquo;s contract price with the seller</span><span>The same &mdash; no markup</span></div>
          <div style={groupHead}>WHAT YOU PAY UP FRONT</div>
          <div style={row}><span>{dpUnknown ? 'Down payment' : `Down payment (${downPct}%)`}</span><span style={bold}>{dpUnknown ? 'Pending' : fmt$(downAmt)}</span></div>
          <div style={row}><span>{`Program fee (${programFeePct}% of the ${feeBasis})`}</span><span style={bold}>{fmt$(programFee)}</span></div>
          <div style={row}><span>Other closing costs and prepaids</span><span style={bold}>{fmt$(closingCosts)}</span></div>
          {bd && <div style={row}><span>{`Discount points (${bd.pts})`}</span><span style={bold}>{fmt$(bd.cost)}</span></div>}
          <div style={big}><span style={{ fontWeight: 800 }}>Total cash to close</span><span data-testid="se-card-cash" style={{ fontWeight: 900 }}>{fmt$(totalCashToClose)}</span></div>
          <div style={groupHead}>MONTHLY ESTIMATE</div>
          <div style={big}><span style={{ fontWeight: 800 }}>Est. monthly payment</span><span data-testid="se-card-monthly" style={{ fontWeight: 900 }}>{fmt$(monthlyPayment)}</span></div>
          <div data-testid="se-card-monthly-lines" data-source={payLines.source}>
            <div style={row}><span>Principal &amp; interest</span><span data-testid="se-card-monthly-pi" style={bold}>{fmt$(payLines.pi)}</span></div>
            <div style={row}><span>Property taxes (est.)</span><span data-testid="se-card-monthly-tax" style={bold}>{fmt$(payLines.tax)}</span></div>
            <div style={row}><span>Homeowners insurance (est.)</span><span data-testid="se-card-monthly-hoi" style={bold}>{fmt$(payLines.hoi)}</span></div>
            {payLines.hoa > 0 && <div style={row}><span>HOA dues</span><span data-testid="se-card-monthly-hoa" style={bold}>{fmt$(payLines.hoa)}</span></div>}
          </div>
          <div style={groupHead}>YOUR FINANCING</div>
          <div style={row}><span>Loan amount · 30 years</span><span style={bold}>{dpUnknown ? 'Pending' : fmt$(loanAmt)}</span></div>
          <div style={row}><span>Interest rate</span>
            {bd ? <span data-testid="se-card-rate" style={{ display: 'flex', gap: 6 }}><span style={{ textDecoration: 'line-through' }}>{origRateStr}</span><span style={{ fontWeight: 800 }}>{displayRateStr}</span></span>
              : <span data-testid="se-card-rate" style={bold}>{displayRateStr}</span>}
          </div>
        </div>
        {maxBuydownPoints > 0 && (
          <div style={{ padding: '10px 16px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button type="button" data-testid="se-card-buydown-toggle" onClick={(e) => { e.stopPropagation(); setBuydownOpen((o) => !o); if (buydownOpen) setBuydownPoints(0); }}
              style={{ width: '100%', padding: '9px 14px', background: '#f7f7f7', border: '1.5px solid #e6e6e6', borderRadius: 10, display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#000', cursor: 'pointer' }}>
              <span>{buydownOpen ? 'Remove rate buydown' : 'Buy down my rate'}</span>
              {!buydownOpen && <span style={{ fontSize: 11, fontWeight: 500 }}>Lower your rate</span>}
            </button>
            {buydownOpen && (
              <div style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {Array.from({ length: maxBuydownPoints + 1 }, (_, i) => i).map((pts) => (
                    <button key={pts} type="button" data-testid={`se-card-points-${pts}`} aria-pressed={buydownPoints === pts}
                      onClick={(e) => { e.stopPropagation(); setBuydownPoints(pts); }}
                      style={{ flex: 1, padding: '7px 2px', borderRadius: 6, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${buydownPoints === pts ? '#3a6ad9' : '#e6e6e6'}`, background: buydownPoints === pts ? '#3a6ad9' : '#fff',
                        color: buydownPoints === pts ? '#fff' : '#000' }}>
                      {ptLabel(pts)}
                    </button>
                  ))}
                </div>
                <div data-testid="se-card-buydown-line" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  {bd && bd.savings > 0 ? `Saves ${fmt$(bd.savings)} a month${bd.breakEven ? `; pays for itself in about ${(bd.breakEven / 12).toFixed(1)} years` : ''}. ` : ''}
                  {`At this price you can buy up to ${ptLabel(maxBuydownPoints)}.`}
                </div>
              </div>
            )}
          </div>
        )}
        <div style={{ padding: '10px 16px 0' }}><p style={{ margin: 0, fontSize: 11.5, textAlign: 'center' }}>No balloon payments. No prepayment penalties.</p></div>
        <div style={{ padding: '10px 16px 16px' }}>
          {showSelect && (dpUnknown ? (
            <div>
              <button type="button" disabled aria-disabled="true" style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', background: '#e6e6e6', color: '#000',
                fontFamily: 'inherit', fontSize: 15, fontWeight: 800 }}>Accept SmartEstimate</button>
              <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>We're finalising your down payment figure. This step will open once your numbers are ready — nothing is owed and nothing has been missed.</div>
            </div>
          ) : (
            <button type="button" onClick={() => onSelect(buydownPoints)} style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', background: '#3a6ad9',
              color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
              {buydownPoints > 0 ? `Accept + ${ptLabel(buydownPoints)}` : 'Accept SmartEstimate'}
            </button>
          ))}
        </div>
        {/* 🔑 SEE ALL ESTIMATED CLOSING COSTS (APPROVED by Hannah 2026-11-01): every figure is one this card already adds into its
            total — no second computation. Below the four groups, not inside YOUR FINANCING, and Doorly's own headings,
            never the LE's section letters (test/smartEstimateFormat). No flood row while the determination is held (test/floodLineHeld). */}
        {showCostDetails && (
          <div style={{ padding: '0 16px 16px' }}>
            <button type="button" data-testid="se-card-costs-toggle" aria-expanded={costsOpen} onClick={(e) => { e.stopPropagation(); setCostsOpen((o) => !o); }}
              style={{ width: '100%', padding: '9px 14px', background: '#f7f7f7', border: '1.5px solid #e6e6e6', borderRadius: 10, display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#000', cursor: 'pointer' }}>
              <span>{CLOSING_COSTS_EXPANDER_LABEL}</span><span aria-hidden="true">{costsOpen ? '×' : '+'}</span>
            </button>
            {costsOpen && (
              <div data-testid="se-card-costs" style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: '10px 12px', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
                <div style={groupHead}>DOORLY&rsquo;S FEE</div>
                <div style={row}><span>{`Program Fee (${programFeePct}% of the ${feeBasis})`}</span><span style={bold}>{fmt$(programFee)}</span></div>
                <div style={groupHead}>THIRD-PARTY SERVICES</div>
                <div style={row}><span>Appraisal Fee</span><span style={bold}>{fmt$(traditionalAppraisalFee)}</span></div>
                <div style={row}><span>Credit Report</span><span style={bold}>{fmt$(Number(creditReportFee))}</span></div>
                {lines.map((l) => <div key={l.label} style={row}><span>{l.label}</span><span style={bold}>{fmt$(Number(l.amount))}</span></div>)}
                <div style={groupHead}>GOVERNMENT RECORDING</div>
                <div style={row}><span>Recording Fees</span><span style={bold}>{fmt$(Number(recordingFee))}</span></div>
                <div style={groupHead}>PREPAIDS AND ESCROW</div>
                <div style={row}><span>Homeowners insurance, first year</span><span style={bold}>{fmt$(hoiFirstYear)}</span></div>
                <div style={row}><span>{`Prepaid property taxes (${Number(B?.prepaidTaxMonths)} months)`}</span><span style={bold}>{fmt$(prepaidTax)}</span></div>
                <div style={row}><span>Initial escrow deposit</span><span style={bold}>{fmt$(escrowDeposit)}</span></div>
                <div style={row}><span>Prepaid interest</span><span style={bold}>{prepaidInterest != null ? fmt$(prepaidInterest) : 'Set by your closing date'}</span></div>
                {bd && <div style={row}><span>{`Discount points (${bd.pts})`}</span><span style={bold}>{fmt$(bd.cost)}</span></div>}
                <div style={row}><span>{dpUnknown ? 'Down payment' : `Down payment (${downPct}%)`}</span><span style={bold}>{dpUnknown ? 'Pending' : fmt$(downAmt)}</span></div>
                <div style={{ ...row, fontWeight: 800 }}><span>Total cash to close</span><span data-testid="se-card-costs-total">{fmt$(totalCashToClose)}</span></div>
                <p style={{ margin: '4px 0 0', fontSize: 11.5, lineHeight: 1.5 }}>{INSPECTION_LINE}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
  <div className="rounded-2xl border overflow-hidden flex flex-col" style={{ borderColor: '#3a6ad9', boxShadow: '0 0 0 2px rgba(58,106,217,0.15)' }}>
  {/* Card header */}
  {/* ⚠️ THE EMPHASIS STYLING STAYS, THE COMPARISON GOES. Option 1 was drawn in
      brand blue and Option 2 in grey — a visual ranking of two things the copy
      was careful to present evenly. With one card the blue is just the card. */}
  <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#3a6ad9' }}>
  <span className="text-sm font-bold" style={{ color: 'white' }}>Your SmartEstimate</span>
  {/* 🚨 THE BADGES ARE GONE WITH THE CHOICE. "Most Popular" ranked one option
      against another — the exact framing Eva is forbidden to use — and "No
      Program Fee" described a structure that no longer exists. Neither means
      anything on a card with nothing beside it. */}
  </div>

  {/* Line items */}
  <div className="divide-y divide-gray-100 bg-white flex-1">


  {/* ── 🚨 SECTION HEADINGS AND AN ORDER THAT IS NOT FORM H-24's ────────────
      § 1026.19(e)(2)(ii) forbids a pre-Loan-Estimate written estimate whose
      headings, content AND FORMAT are substantially similar to H-24, and an
      on-screen estimate is written for that purpose. This card had NO headings
      and one flat list running Purchase Price → Down Payment → Loan Amount →
      Interest Rate → Term → fees → Cash to Close → P&I → Monthly Payment.
      Loan Amount immediately followed by Interest Rate is H-24's Loan Terms
      box; Cash to Close followed by P&I and a total is its Projected Payments
      and Costs at Closing read in sequence.

      ⚠️ NO FIGURE CHANGED. Every value below is the value it was. The headings
      are Doorly's own words, and the four groups match the signed PDF exactly
      — a borrower sees both documents and they should not disagree on shape.
      Kept out of a shared constant on purpose: the two render in different
      technologies with different row widgets, and the thing that must match is
      the grouping, which is asserted in backend/test/smartEstimateFormat.test.js. */}
  <div className="px-4 pt-3 pb-1">
  <span className="text-[10px] font-bold text-gray-400 tracking-wider">YOUR PRICE</span>
  </div>
  {/* Your Purchase Price with tooltip */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500 flex items-center">
    Your Purchase Price
    <Tooltip text={PURCHASE_PRICE_TOOLTIP} />
  </span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(purchasePrice)}</span>
  </div>
  {/* 🔑 (Jesse, 2026-10-21) The price IS Doorly's contract price — said once, plainly; no resale adjustment row. */}
  <div className="flex items-center justify-between px-4 py-2.5" data-testid="smartestimate-contract-price">
  <span className="text-xs text-gray-500">Doorly&rsquo;s contract price with the seller</span>
  <span className="text-xs font-semibold text-gray-800">The same &mdash; no markup</span>
  </div>




  <div className="px-4 pt-3 pb-1">
  <span className="text-[10px] font-bold text-gray-400 tracking-wider">WHAT YOU PAY UP FRONT</span>
  </div>
  {/* Program Fee with tooltip */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500 flex items-center">
    Program Fee:
    <Tooltip text={PROGRAM_FEE_TOOLTIP} />
  </span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(programFee)} <span className="text-gray-400">({programFeePct}% of the {programFeeBasis === 'price' ? 'purchase price' : 'loan amount'})</span></span>
  </div>

  {/* Appraisal — the LE/CD label (backend constants/disclosureFeeLabels; test/leSurfaceAgreement holds them equal) */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500" data-testid="se-card-appraisal-label">Appraisal Fee</span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(traditionalAppraisalFee)}</span>
  </div>

  {/* Credit Report, title (Section B) and recording — itemized as the LE carries them */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Credit Report</span>
  <span className="text-xs font-semibold text-gray-800">{fmtFee(creditReportFee)}</span>
  </div>
  {lines.map((l) => (
  <div key={l.key} className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">{l.label}</span>
  <span className="text-xs font-semibold text-gray-800">{fmtFee(l.amount)}</span>
  </div>
  ))}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Recording Fees</span>
  <span className="text-xs font-semibold text-gray-800">{fmtFee(recordingFee)}</span>
  </div>

  {/* Homeowners Insurance (1st year) */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Homeowners Insurance (1st year)</span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(hoiFirstYear)}</span>
  </div>
  {/* The LE's Section F prepaid interest and taxes, and its Section G escrow deposit */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Prepaid Interest</span>
  <span className="text-xs font-semibold text-gray-800">{prepaidInterest === null ? 'Set by closing date' : fmt$(prepaidInterest)}</span>
  </div>
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Prepaid Property Taxes</span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(prepaidTax)}</span>
  </div>
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Initial Escrow Deposit</span>
  <span className="text-xs font-semibold text-gray-800">{fmt$(escrowDeposit)}</span>
  </div>
  {/* Down Payment */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500 flex items-center">
    {dpUnknown ? 'Down Payment' : `Down Payment (${downPct}%)`}
    <Tooltip text={DOWN_PAYMENT_TOOLTIP} />
  </span>
  <span className="text-xs font-semibold text-gray-800">{dpUnknown ? 'Pending' : fmt$(downAmt)}</span>
  </div>

  {/* Divider */}
  <div className="mx-4 my-1 border-t border-dashed border-gray-200" />

  {/* Total Cash to Close */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs font-semibold text-gray-700 flex items-center">
    Total Cash to Close
    <Tooltip text={TOTAL_CASH_TOOLTIP} />
  </span>
  <span className="font-bold" style={{ fontSize: 15, color: '#3a6ad9' }}>{fmt$(totalCashToClose)}</span>
  </div>

  <div className="px-4 pt-3 pb-1">
  <span className="text-[10px] font-bold text-gray-400 tracking-wider">MONTHLY ESTIMATE</span>
  </div>
  {/* Monthly Payment */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs font-semibold text-gray-700 flex items-center">
    Est. Monthly Payment
    <Tooltip text={MONTHLY_PAYMENT_TOOLTIP} />
  </span>
  <span className="font-bold" style={{ fontSize: 15, color: '#3a6ad9' }}>{fmt$(monthlyPayment)}</span>
  </div>

  {/* ⚠️ THE TOTAL LEADS, THE BREAKDOWN FOLLOWS. H-24 runs P&I → escrow →
      total; leading with the figure the borrower asked for is a different
      shape and the more useful one. */}
  {/* Monthly Principal & Interest */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs font-semibold text-gray-700 flex items-center">
    Monthly Principal &amp; Interest
  </span>
  <span className="font-bold" style={{ fontSize: 15, color: '#3a6ad9' }}>{fmt$(shownPI)}</span>
  </div>

  <div className="px-4 pt-3 pb-1">
  <span className="text-[10px] font-bold text-gray-400 tracking-wider">YOUR FINANCING</span>
  </div>
  {/* Loan Amount */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Loan Amount</span>
  <span className="text-xs font-semibold text-gray-800">{dpUnknown ? 'Pending' : fmt$(loanAmt)}</span>
  </div>

  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500">Term</span>
  <span className="text-xs font-semibold text-gray-800">30 years</span>
  </div>

  {/* Interest Rate with tooltip */}
  <div className="flex items-center justify-between px-4 py-2.5">
  <span className="text-xs text-gray-500 flex items-center">
    Interest Rate
    <Tooltip text={INTEREST_RATE_TOOLTIP} />
  </span>
  <span className="text-xs font-semibold text-gray-800">
    {bd ? (<><span style={{ textDecoration: 'line-through', color: '#9ca3af', marginRight: 6, fontWeight: 400, fontSize: '11px' }}>{origRateStr}</span><span style={{ color: '#3a6ad9', fontWeight: 800 }}>{displayRateStr}</span></>) : displayRateStr}
  </span>
  </div>
  </div>

  {/* Buy Down My Rate */}
  <div className="px-4 py-2">
  {/* 🚨 NO CONTROL AT ALL WHERE NO POINTS MAY BE BOUGHT. A "Buy Down My Rate"
      button that opens onto a single "None" option is a dead affordance, and on
      the pre-approval preview there is no file to cap against yet. */}
  {maxBuydownPoints > 0 && (<>
  <button onClick={(e) => { e.stopPropagation(); setBuydownOpen(o => !o); if (buydownOpen) setBuydownPoints(0); }}
    style={{ width: '100%', padding: '9px 14px', background: buydownOpen ? '#edf1fb' : '#edf1fb', border: `1.5px solid ${buydownOpen ? '#c5d3f5' : '#c5d3f5'}`, borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#3a6ad9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.15s' }}>
    <span>{buydownOpen ? 'Remove Rate Buydown' : 'Buy Down My Rate'}</span>
    {!buydownOpen && <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '500' }}>Lower your rate</span>}
  </button>
  {buydownOpen && (
    <div style={{ background: '#f8faff', border: '1px solid #c5d3f5', borderRadius: '10px', padding: '12px', marginTop: '8px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Discount Points</div>
      {/* 🚨 ONLY UP TO THE CAP. This was [0, 1, 2, 3, 4] for every borrower. */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        {Array.from({ length: maxBuydownPoints + 1 }, (_, i) => i).map(pts => (
          <button key={pts} onClick={(e) => { e.stopPropagation(); setBuydownPoints(pts); }}
            style={{ flex: 1, padding: '6px 2px', borderRadius: '6px', border: `1.5px solid ${buydownPoints === pts ? '#3a6ad9' : '#d1d5db'}`, background: buydownPoints === pts ? '#3a6ad9' : '#fff', color: buydownPoints === pts ? '#fff' : '#374151', fontWeight: '700', fontSize: '12px', cursor: 'pointer', textAlign: 'center' }}>
            {pts === 0 ? 'None' : `${pts}pt`}
          </button>
        ))}
      </div>
      {/* ⚠️ THE LIMIT IS EXPLAINED WHERE IT BITES, in the server's own plain
          words — no citation, no "contact us". A control that simply stops at
          two, with no reason, reads as a bug. */}
      {buydownCap && buydownCap.explanation && (
        <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.5, marginBottom: '8px' }}>
          {buydownCap.explanation}
        </div>
      )}
      {buydownPoints > 0 && bd && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>New Rate</span><span style={{ fontWeight: '700', color: '#3a6ad9' }}>{formatRate(bd.newRate)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>Points Cost</span><span style={{ fontWeight: '600', color: '#7a5200' }}>+${bd.cost.toLocaleString()}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>Monthly Savings</span><span style={{ fontWeight: '600', color: '#2d6a0a' }}>-${Math.round(bd.savings).toLocaleString()}/mo</span></div>
          {bd.breakEven && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>Break-even</span><span style={{ fontWeight: '600', color: '#374151' }}>{bd.breakEven} mo (~{(bd.breakEven / 12).toFixed(1)} yrs)</span></div>}
        </div>
      )}
      {buydownPoints === 0 && <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>Select points to see your reduced rate</div>}
    </div>
  )}
  </>)}
  </div>

  {/* Fine print */}
  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
  <p className="text-xs italic" style={{ color: '#9ca3af' }}>No balloon payments. No prepayment penalties.</p>
  </div>

  {/* Select button */}
  <div className="px-4 py-3 bg-white border-t border-gray-100">
  {/* 🚨 STILL AN EXPLICIT ACT. There is nothing to choose between, and the
      borrower must still accept and sign — so the button stays and the verb
      changes from "Select" to "Accept". Auto-accepting because there is only
      one thing on screen would be the silent-selection this change was told
      not to make.

      ⚠️ onSelect STILL CARRIES THE BUYDOWN POINTS. Rate buydown is unchanged
      and is chosen on this card; dropping the argument would have made the
      accept button quietly discard the points the borrower just bought. */}
  {showSelect && (dpUnknown ? (
    /* ⚠️ DISABLED, WITH THE REASON, NOT A BUTTON THAT RETURNS 409. The server
       refuses this acceptance (DOWN_PAYMENT_NOT_SET) because there is no down
       payment percent to record. A control that still looks ready invites a
       borrower to draw a signature and submit it before being told the terms
       were not ready — the refusal must be visible BEFORE the signature, not
       after it. */
    <div>
      <button disabled aria-disabled="true"
        className="w-full h-9 rounded-xl text-sm font-semibold"
        style={{ backgroundColor: '#e5e7eb', color: '#9ca3af', border: 'none', cursor: 'not-allowed' }}>
        Accept SmartEstimate
      </button>
      <div className="mt-2 text-xs leading-relaxed" style={{ color: '#7a5200' }}>
        ⏳ We're finalising your down payment figure. This step will open once your numbers are
        ready — nothing is owed and nothing has been missed.
      </div>
    </div>
  ) : (
    <button
    onClick={() => onSelect(buydownPoints)}
    className="w-full h-9 rounded-xl text-sm font-semibold transition-all"
    style={{ backgroundColor: '#3a6ad9', color: 'white', border: 'none' }}
  >
    {buydownPoints > 0 ? `Accept + ${buydownPoints} Pt${buydownPoints > 1 ? 's' : ''}` : 'Accept SmartEstimate'}
  </button>
  ))}
  </div>
  </div>
  );
}

export { SmartEstimateOptionCard };
